import type { IconButtonProps } from '@mui/material/IconButton';
import type { LangCode } from 'src/locales';

import { useCallback } from 'react';
import { useBoolean, usePopover } from 'minimal-shared/hooks';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Avatar from '@mui/material/Avatar';
import Drawer from '@mui/material/Drawer';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

import { RouterLink } from 'src/routes/components';

import { allLangs, useTranslate } from 'src/locales';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { FlagIcon } from 'src/components/flag-icon';
import { Scrollbar } from 'src/components/scrollbar';
import { AnimateBorder } from 'src/components/animate';
import { useSettingsContext } from 'src/components/settings';
import { CustomPopover } from 'src/components/custom-popover';

import { useAuthContext } from 'src/auth/hooks';

import { AccountButton } from './account-button';
import { SignOutButton } from './sign-out-button';

// ----------------------------------------------------------------------

export type AccountDrawerProps = IconButtonProps & {
  data?: {
    label: string;
    href: string;
    icon?: React.ReactNode;
    info?: React.ReactNode;
    translationKey?: string;
    isLanguage?: boolean;
    isLayout?: boolean;
  }[];
};

export function AccountDrawer({ data = [], sx, ...other }: AccountDrawerProps) {
  const { user } = useAuthContext();

  const { t, onChangeLang, currentLang } = useTranslate();
  const settings = useSettingsContext();

  const displayName =
    (user?.firstName || user?.lastName
      ? [user?.firstName, user?.lastName].filter(Boolean).join(' ')
      : user?.displayName) ||
    user?.username ||
    user?.email ||
    '';

  const email = user?.email ?? '';
  const photoURL = user?.imageUrl ?? user?.photoURL ?? '';

  const { value: open, onFalse: onClose, onTrue: onOpen } = useBoolean();
  const languagePopover = usePopover();

  const handleChangeLang = useCallback(
    (lang: LangCode) => {
      onChangeLang(lang);
      languagePopover.onClose();
    },
    [onChangeLang, languagePopover]
  );

  const handleLayoutClick = useCallback(() => {
    settings.onToggleDrawer();
  }, [settings]);

  const renderAvatar = () => (
    <AnimateBorder
      sx={{ mb: 2, p: '6px', width: 96, height: 96, borderRadius: '50%' }}
      slotProps={{
        primaryBorder: { size: 120, sx: { color: 'primary.main' } },
      }}
    >
      <Avatar src={photoURL} alt={displayName} sx={{ width: 1, height: 1 }}>
        {displayName?.charAt(0).toUpperCase()}
      </Avatar>
    </AnimateBorder>
  );

  const renderLanguagePopover = () => (
    <CustomPopover
      open={languagePopover.open}
      anchorEl={languagePopover.anchorEl}
      onClose={languagePopover.onClose}
    >
      <MenuList sx={{ width: 160, minHeight: 72 }}>
        {allLangs.map((lang) => (
          <MenuItem
            key={lang.value}
            selected={lang.value === currentLang.value}
            onClick={() => handleChangeLang(lang.value as LangCode)}
          >
            <FlagIcon code={lang.countryCode} />
            {lang.label}
          </MenuItem>
        ))}
      </MenuList>
    </CustomPopover>
  );

  const renderList = () => (
    <MenuList
      disablePadding
      sx={[
        (theme) => ({
          py: 3,
          px: 2.5,
          borderTop: `dashed 1px ${theme.vars.palette.divider}`,
          borderBottom: `dashed 1px ${theme.vars.palette.divider}`,
          '& li': { p: 0 },
        }),
      ]}
    >
      {data.map((option) => {
        if (option.isLanguage) {
          return (
            <MenuItem key={option.label}>
              <Box
                onClick={languagePopover.onOpen}
                sx={{
                  p: 1,
                  width: 1,
                  display: 'flex',
                  typography: 'body2',
                  alignItems: 'center',
                  color: 'text.secondary',
                  cursor: 'pointer',
                  '& svg': { width: 24, height: 24 },
                  '&:hover': { color: 'text.primary' },
                }}
              >
                <FlagIcon code={currentLang.countryCode} />
                <Box component="span" sx={{ ml: 2 }}>
                  {currentLang.label}
                </Box>
              </Box>
            </MenuItem>
          );
        }

        if (option.isLayout) {
          return (
            <MenuItem key={option.label}>
              <Box
                onClick={handleLayoutClick}
                sx={{
                  p: 1,
                  width: 1,
                  display: 'flex',
                  typography: 'body2',
                  alignItems: 'center',
                  color: 'text.secondary',
                  cursor: 'pointer',
                  '& svg': { width: 24, height: 24 },
                  '&:hover': { color: 'text.primary' },
                }}
              >
                {option.icon}
                <Box component="span" sx={{ ml: 2 }}>
                  {option.translationKey ? t(option.translationKey) : option.label}
                </Box>
              </Box>
            </MenuItem>
          );
        }

        return (
          <MenuItem key={option.label}>
            <Link
              component={RouterLink}
              href={option.href}
              color="inherit"
              underline="none"
              onClick={onClose}
              sx={{
                p: 1,
                width: 1,
                display: 'flex',
                typography: 'body2',
                alignItems: 'center',
                color: 'text.secondary',
                '& svg': { width: 24, height: 24 },
                '&:hover': { color: 'text.primary' },
              }}
            >
              {option.icon}

              <Box component="span" sx={{ ml: 2 }}>
                {option.translationKey ? t(option.translationKey) : option.label}
              </Box>

              {option.info && (
                <Label color="error" sx={{ ml: 1 }}>
                  {option.info}
                </Label>
              )}
            </Link>
          </MenuItem>
        );
      })}
    </MenuList>
  );

  return (
    <>
      <AccountButton
        onClick={onOpen}
        photoURL={photoURL}
        displayName={displayName}
        sx={sx}
        {...other}
      />

      <Drawer
        open={open}
        onClose={onClose}
        anchor="right"
        slotProps={{
          backdrop: { invisible: true },
          paper: { sx: { width: 320 } },
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            top: 12,
            left: 12,
            zIndex: 9,
            position: 'absolute',
          }}
        >
          <Iconify icon="mingcute:close-line" />
        </IconButton>

        <Scrollbar>
          <Box
            sx={{
              pt: 8,
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
            }}
          >
            {renderAvatar()}

            <Typography variant="subtitle1" noWrap sx={{ mt: 2 }}>
              {displayName}
            </Typography>

            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} noWrap>
              {email}
            </Typography>
          </Box>

          {renderList()}
        </Scrollbar>

        <Box sx={{ p: 2.5 }}>
          <SignOutButton onClose={onClose} />
        </Box>
      </Drawer>

      {renderLanguagePopover()}
    </>
  );
}
